// vg-binding: P7.D-150
// vg-binding: P18.D-28
//
// apps/vendor/src/features/orders/detail/ActionBar.tsx
//
// Phase 18 Task 34 — the 409 that means "refetch", not "you failed"
// -----------------------------------------------------------------
// Every button here sends `If-Match: <item_version>` (P18.D-28), and the same
// row can now be advanced by the vendor's OWN system through the machine lane
// between the detail poll (30s) and the click. The server answers 409 with the
// current version (E-19) or the current state (E-20/E-24), which is not a
// failure the user caused and not one they can fix by pressing harder: the
// local copy is simply behind. So a 409 refetches the detail + timeline and
// says so, instead of printing a version-mismatch sentence next to a button
// still holding the stale number (E-43 — the machine acted while staff had the
// portal open).
//
// 3-button chain amendment:
//
//   accepted        → [Start Production]
//   in_production   → [QC Check]            (opens QCModal)
//   quality_check   → [QC Pass | QC Fail]   (opens QCModal in pass-or-fail mode)
//   ready_to_ship   → [Mark Shipped]        (opens TrackingInputModal)
//   shipped         → [Mark Completed]
//
// Modals (Task 21) handle the actual transition mutation; this component
// orchestrates which one to open + back-order CTA when admin enabled it.

import { useState } from 'react';

import { cn } from '@printway/ui';

import { useTranslation } from '../../../lib/i18n/index.js';
import {
  useBackOrder,
  useTransitionItem,
  type OrderItemDetail,
  type OrdersApiError,
} from '../api.js';
import { DimInputModal } from './DimInputModal.js';
import { QCModal } from './QCModal.js';
import { TrackingInputModal } from './TrackingInputModal.js';

interface Props {
  detail: OrderItemDetail;
  onMutated: () => void;
}

type ModalKind = 'none' | 'dim' | 'qc-pass' | 'qc-fail' | 'tracking';

export function ActionBar({ detail, onMutated }: Props): JSX.Element {
  const { t } = useTranslation('orders');
  const [modal, setModal] = useState<ModalKind>('none');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const transitionM = useTransitionItem();
  const backOrderM = useBackOrder();

  /**
   * A 409 on any of these writes means our `item_version` / state is stale —
   * someone or something moved the row first. `current_version` /
   * `current_state` ride along on the error precisely so the client can
   * resynchronise (P18.D-28, E-19/E-20/E-21/E-24).
   */
  function isStaleConflict(err: OrdersApiError): boolean {
    return (
      err.statusCode === 409 ||
      err.currentVersion !== undefined ||
      err.currentState !== undefined
    );
  }

  /** Message for a failed write. A conflict gets the "we refreshed" copy. */
  function conflictAwareMessage(err: OrdersApiError, stale: boolean): string {
    if (stale) return t('actionBar.conflictRefreshed');
    return err.userMessage ?? err.message ?? t('actionBar.transitionFailed');
  }

  async function fire(
    args: Parameters<typeof transitionM.mutateAsync>[0],
  ): Promise<void> {
    setErrorMsg(null);
    try {
      await transitionM.mutateAsync(args);
      onMutated();
    } catch (err) {
      if (err instanceof Error) {
        const e = err as OrdersApiError;
        const stale = isStaleConflict(e);
        setErrorMsg(conflictAwareMessage(e, stale));
        // Pull the canonical row + timeline back down so the next click sends
        // the version the server actually holds. Without this the button keeps
        // re-sending the stale one and every retry 409s again.
        if (stale) onMutated();
      }
    }
  }

  async function fireBackOrder(): Promise<void> {
    setErrorMsg(null);
    try {
      await backOrderM.mutateAsync({
        order_id: detail.order_id,
        item_id: detail.item_id,
        item_version: detail.item_version,
      });
      onMutated();
    } catch (err) {
      const e = err as OrdersApiError;
      const stale = isStaleConflict(e);
      setErrorMsg(conflictAwareMessage(e, stale));
      if (stale) onMutated();
    }
  }

  const status = detail.status;

  return (
    <>
      <section
        data-testid="order-detail-action-bar"
        className="rounded-xl border border-pw-border bg-pw-bg p-5 shadow-sm"
      >
        <h3 className="mb-3 text-sm font-semibold text-pw-text">{t('actionBar.title')}</h3>

        {errorMsg ? (
          <p
            className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            role="alert"
            data-testid="order-detail-action-error"
          >
            {errorMsg}
          </p>
        ) : null}

        <div className="flex flex-wrap items-center gap-2">
          {/* Accept step removed — assignments are auto-accepted on (re)assign
              (BE: routeItem / admin-reassign call autoAcceptAssignmentInTransaction),
              so items land in `accepted` and the vendor goes straight to Start
              Production. No manual Accept button anymore. */}
          {status === 'accepted' ? (
            <Btn
              testId="action-start-production"
              label={t('actionBar.actionStartProduction')}
              onClick={() => setModal('dim')}
              disabled={transitionM.isPending}
              primary
            />
          ) : null}

          {status === 'in_production' ? (
            <Btn
              testId="action-qc-check"
              label={t('actionBar.actionQcCheck')}
              onClick={() =>
                void fire({
                  order_id: detail.order_id,
                  item_id: detail.item_id,
                  item_version: detail.item_version,
                  to_state: 'quality_check',
                })
              }
              disabled={transitionM.isPending}
              primary
            />
          ) : null}

          {status === 'quality_check' ? (
            <>
              <Btn
                testId="action-qc-pass"
                label={t('actionBar.actionQcPass')}
                onClick={() => setModal('qc-pass')}
                disabled={transitionM.isPending}
                primary
              />
              <Btn
                testId="action-qc-fail"
                label={t('actionBar.actionQcFail')}
                onClick={() => setModal('qc-fail')}
                disabled={transitionM.isPending}
              />
            </>
          ) : null}

          {status === 'ready_to_ship' ? (
            <Btn
              testId="action-mark-shipped"
              label={t('actionBar.actionMarkShipped')}
              onClick={() => setModal('tracking')}
              disabled={transitionM.isPending}
              primary
            />
          ) : null}

          {status === 'shipped' ? (
            <Btn
              testId="action-mark-completed"
              label={t('actionBar.actionMarkCompleted')}
              onClick={() =>
                void fire({
                  order_id: detail.order_id,
                  item_id: detail.item_id,
                  item_version: detail.item_version,
                  to_state: 'completed',
                })
              }
              disabled={transitionM.isPending}
              primary
            />
          ) : null}

          {detail.back_order_enabled && !detail.back_order_used ? (
            <Btn
              testId="action-back-order"
              label={t('actionBar.actionBackOrder')}
              onClick={() => void fireBackOrder()}
              disabled={backOrderM.isPending}
            />
          ) : null}

          {/* P7.D-148 (4.4 Amendment #2, 2026-08-10): no vendor self-reject.
              The R6-1 unconditional reject button was a spec regression; the
              only escape is the admin-gated Back Order button above. */}
        </div>
      </section>

      <DimInputModal
        open={modal === 'dim'}
        orderId={detail.order_id}
        itemId={detail.item_id}
        onCancel={() => setModal('none')}
        onConfirm={async () => {
          await fire({
            order_id: detail.order_id,
            item_id: detail.item_id,
            item_version: detail.item_version,
            to_state: 'in_production',
          });
          setModal('none');
        }}
      />
      <QCModal
        mode={modal === 'qc-fail' ? 'fail' : 'pass'}
        open={modal === 'qc-pass' || modal === 'qc-fail'}
        onCancel={() => setModal('none')}
        onConfirm={async (reason) => {
          if (modal === 'qc-pass') {
            await fire({
              order_id: detail.order_id,
              item_id: detail.item_id,
              item_version: detail.item_version,
              to_state: 'ready_to_ship',
              metadata: { qc_pass: true },
            });
          } else {
            await fire({
              order_id: detail.order_id,
              item_id: detail.item_id,
              item_version: detail.item_version,
              to_state: 'in_production',
              reason,
              metadata: { qc_pass: false },
            });
          }
          setModal('none');
        }}
      />
      <TrackingInputModal
        open={modal === 'tracking'}
        onCancel={() => setModal('none')}
        onConfirm={async (tracking) => {
          await fire({
            order_id: detail.order_id,
            item_id: detail.item_id,
            item_version: detail.item_version,
            to_state: 'shipped',
            metadata: { tracking },
          });
          setModal('none');
        }}
      />
    </>
  );
}

function Btn({
  testId,
  label,
  onClick,
  disabled,
  primary,
}: {
  testId: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  primary?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'inline-flex h-9 items-center rounded-md px-4 text-sm font-semibold transition-colors',
        primary
          ? 'bg-pw-primary text-white hover:bg-pw-primary-strong'
          : 'border border-pw-border bg-pw-surface text-pw-text hover:bg-pw-bg',
        disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      {label}
    </button>
  );
}

export default ActionBar;
